import React, { useState } from "react";
import { useMutation } from "@apollo/client";
import { UPDATE_CATERER } from "../../../Graphql/mutations";
import { GET_ONE_CATERER } from "../../../Graphql/queries";

interface IEmailEntry {
  name: string;
  email: string;
}

interface IExtraEmailProps {
  catererId: string;
  emailForMonthlyStmt: string;
  loginEmail: string;
  loginName: string;
}

const MAX_ROWS = 4;

/**
 * Parse the stored value into an array of { name, email } entries.
 * Handles both new JSON format and legacy comma-separated emails.
 */
function parseEntries(raw: string): IEmailEntry[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed;
  } catch {
    // Legacy format: comma-separated emails
    return raw
      .split(",")
      .map((e) => e.trim())
      .filter(Boolean)
      .map((email) => ({ name: "", email }));
  }
  return [];
}

function serializeEntries(entries: IEmailEntry[]): string {
  const filtered = entries.filter((e) => e.email.trim());
  return JSON.stringify(filtered);
}

function validateEmail(value: string): boolean {
  if (!value) return false;
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(value);
}

export function ExtraEmail({
  catererId,
  emailForMonthlyStmt,
  loginEmail,
  loginName,
}: IExtraEmailProps) {
  const initialEntries = parseEntries(emailForMonthlyStmt);
  // If no entries exist, pre-fill with the login user
  const defaultEntries =
    initialEntries.length > 0
      ? initialEntries
      : loginEmail
        ? [{ name: loginName || "", email: loginEmail }]
        : [];

  const [isEditing, setIsEditing] = useState(false);
  const [rows, setRows] = useState<IEmailEntry[]>(defaultEntries);
  const [error, setError] = useState("");

  const [updateCaterer, { loading }] = useMutation(UPDATE_CATERER, {
    refetchQueries: [
      {
        query: GET_ONE_CATERER,
        variables: { getOneCatererId: catererId, agsType: "id" },
      },
    ],
  });

  // Pad rows to always show up to MAX_ROWS when editing
  function getEditableRows(): IEmailEntry[] {
    const padded = [...rows];
    while (padded.length < MAX_ROWS) {
      padded.push({ name: "", email: "" });
    }
    return padded;
  }

  function handleRowChange(index: number, field: "name" | "email", value: string) {
    setError("");
    const updated = getEditableRows().map((row, i) =>
      i === index ? { ...row, [field]: value } : row
    );
    // Keep only rows that have at least some content, plus empty placeholders
    setRows(updated);
  }

  async function handleSave() {
    setError("");
    // Only validate rows that have content
    const editableRows = getEditableRows();
    const filledRows = editableRows.filter((r) => r.email.trim() || r.name.trim());

    for (const row of filledRows) {
      if (!row.email.trim()) {
        setError("Each entry must have an email address");
        return;
      }
      if (!validateEmail(row.email.trim())) {
        setError(`"${row.email}" is not a valid email address`);
        return;
      }
    }

    // Check for duplicate emails
    const emailSet = new Set<string>();
    for (const row of filledRows) {
      const lower = row.email.trim().toLowerCase();
      if (emailSet.has(lower)) {
        setError(`Duplicate email: ${row.email}`);
        return;
      }
      emailSet.add(lower);
    }

    const cleanedRows = filledRows.map((r) => ({
      name: r.name.trim(),
      email: r.email.trim(),
    }));

    try {
      await updateCaterer({
        variables: {
          id: catererId,
          data: {
            emailForMonthlyStmt: serializeEntries(cleanedRows),
          },
        },
      });
      setRows(cleanedRows);
      setIsEditing(false);
    } catch (err) {
      setError("Failed to update. Please try again.");
    }
  }

  function handleCancel() {
    setRows(defaultEntries);
    setError("");
    setIsEditing(false);
  }

  const displayEntries = defaultEntries;

  return (
    <div className="location-details-tile">
      <div className="ld-titles">
        <h4 className="h4">
          <img
            src="../assets/images/icon-mail.svg"
            className="img-fluid"
            alt="Email Icon"
          />
          Email Addresses for Statements
        </h4>
        {!isEditing && (
          <a className="ld-edit" onClick={() => setIsEditing(true)}>
            <img
              src="../assets/images/icon-edit.svg"
              className="img-fluid"
              alt="Edit Icon"
            />
            Edit
          </a>
        )}
      </div>
      <div className="ld-business-detail">
        {isEditing ? (
          <div>
            <table className="table table-bordered" style={{ marginBottom: "10px" }}>
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email Address</th>
                </tr>
              </thead>
              <tbody>
                {getEditableRows().map((row, index) => (
                  <tr key={index}>
                    <td style={{ padding: "4px" }}>
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={row.name}
                        onChange={(e) => handleRowChange(index, "name", e.target.value)}
                        placeholder="Enter name"
                        disabled={loading}
                      />
                    </td>
                    <td style={{ padding: "4px" }}>
                      <input
                        type="email"
                        className="form-control form-control-sm"
                        value={row.email}
                        onChange={(e) => handleRowChange(index, "email", e.target.value)}
                        placeholder="Enter email address"
                        disabled={loading}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {error && (
              <span
                className="error-msg"
                style={{ color: "red", fontSize: "12px", display: "block", marginBottom: "8px" }}
              >
                {error}
              </span>
            )}
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleSave}
                disabled={loading}
              >
                {loading ? "Saving..." : "Save"}
              </button>
              <button
                className="btn btn-outline-secondary btn-sm"
                onClick={handleCancel}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </div>
        ) : (
          <table className="table table-bordered" style={{ marginBottom: 0 }}>
            <thead>
              <tr>
                <th>Name</th>
                <th>Email Address</th>
              </tr>
            </thead>
            <tbody>
              {displayEntries.length > 0 ? (
                displayEntries.map((entry, index) => (
                  <tr key={index}>
                    <td>{entry.name || "-"}</td>
                    <td>{entry.email}</td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={2} style={{ color: "#999", fontStyle: "italic" }}>
                    No email addresses configured
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
